'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Video {
  id: number;
  title: string;
  duration: string;
  thumbnail: string;
  thumbnailAlt: string;
  description: string;
  category: string;
}

interface VideoSectionProps {
  videos: Video[];
}

export default function VideoSection({ videos }: VideoSectionProps) {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const categories = ['All', 'Getting Started', 'Advanced', 'Tips & Tricks'];

  const filteredVideos = activeCategory === 'All' 
    ? videos 
    : videos.filter(video => video.category === activeCategory);

  return (
    <section className="py-20 px-4 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Video Tutorials
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Watch our comprehensive video guides to master Signaly and maximize your trading success.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-cyan-glow dark:shadow-cyan-glow'
                  : 'bg-card text-foreground border border-border hover:bg-muted'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="bg-card rounded-lg overflow-hidden border border-border hover:shadow-lg dark:hover:shadow-cyan-glow/20 transition-all duration-200 cursor-pointer"
              onClick={() => setSelectedVideo(video)}
            >
              <div className="relative">
                <AppImage
                  src={video.thumbnail}
                  alt={video.thumbnailAlt}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors duration-200">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="PlayIcon" size={32} className="text-primary-foreground ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs text-white">
                  {video.duration}
                </div>
              </div>
              
              <div className="p-4">
                <div className="inline-block px-2 py-1 bg-primary/10 dark:bg-primary/20 rounded text-xs font-medium text-primary mb-2">
                  {video.category}
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {video.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {video.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {selectedVideo && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg max-w-4xl w-full overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="text-xl font-heading font-semibold text-foreground">
                  {selectedVideo.title}
                </h3>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="p-2 hover:bg-muted rounded-full transition-colors duration-200"
                >
                  <Icon name="XMarkIcon" size={24} className="text-foreground" />
                </button>
              </div>
              
              <div className="aspect-video bg-black flex items-center justify-center">
                <div className="text-center space-y-4">
                  <Icon name="PlayCircleIcon" size={64} className="text-primary mx-auto" />
                  <p className="text-white">Video player would be embedded here</p>
                  <p className="text-sm text-gray-400">{selectedVideo.description}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}